import React from "react";
import type { AttributeOption } from "../types";
import styles from "./AttributeSuggestionList.module.scss";

export interface AttributeSuggestionListProps {
  items: AttributeOption[];
  command: (item: AttributeOption) => void;
  query: string;
  /** DOM id for the listbox, which the editor points at with `aria-controls`. */
  listId: string;
  /** Reports the highlighted option's DOM id, for the editor's `aria-activedescendant`. */
  onActiveIdChange?: (id: string | undefined) => void;
}

export interface AttributeSuggestionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

/** Keys that pick the highlighted attribute. Space is left alone: it types, since labels are multi-word. */
const SELECT_KEYS = new Set(["Enter", "Tab"]);

/**
 * Inline list for the `@` attribute suggestion (ENG-591).
 *
 * Rendered by `@tiptap/suggestion` through a `ReactRenderer` (a detached React
 * root), so it takes everything it needs as props rather than reading app
 * context. The editor keeps focus the whole time: keys arrive through the
 * `onKeyDown` imperative handle, and the highlighted option is announced via
 * `aria-activedescendant` on the editor. Arrow keys move, Enter or Tab picks,
 * Escape closes (handled by the plugin).
 *
 * Labels wrap to their full length, as in `AttributePopover`: a cut-off
 * sentence-length label may not be tellable apart from another.
 */
export const AttributeSuggestionList = React.forwardRef<AttributeSuggestionListRef, AttributeSuggestionListProps>(
  ({ items, command, listId, onActiveIdChange }, ref) => {
    const [active, setActive] = React.useState(0);
    const itemRefs = React.useRef<Array<HTMLDivElement | null>>([]);
    const optionId = (index: number) => `${listId}-option-${index}`;

    React.useEffect(() => setActive(0), [items]);

    React.useEffect(() => {
      itemRefs.current[active]?.scrollIntoView?.({ block: "nearest" });
      onActiveIdChange?.(items.length > 0 ? optionId(active) : undefined);
    }, [active, items.length, onActiveIdChange]);

    React.useImperativeHandle(
      ref,
      () => ({
        onKeyDown: ({ event }) => {
          if (items.length === 0) return false;
          if (event.key === "ArrowUp") {
            setActive((current) => (current + items.length - 1) % items.length);
            return true;
          }
          if (event.key === "ArrowDown") {
            setActive((current) => (current + 1) % items.length);
            return true;
          }
          if (SELECT_KEYS.has(event.key) && !event.shiftKey) {
            const item = items[active];
            if (item) command(item);
            return true;
          }
          return false;
        },
      }),
      [items, active, command],
    );

    if (items.length === 0) {
      return <output className={styles.empty}>No matching attributes</output>;
    }

    return (
      // biome-ignore lint/a11y/useFocusableInteractive: focus stays in the editor, which points here with aria-activedescendant.
      <div
        id={listId}
        className={styles.list}
        // biome-ignore lint/a11y/useSemanticElements: a <select> can't be driven from the editor; this is an ARIA combobox list.
        role="listbox"
        aria-label="Attributes"
      >
        {items.map((item, index) => (
          // Options, not buttons: focus stays in the editor, and clicks are handled on mousedown
          // so the editor never blurs mid-pick.
          // biome-ignore lint/a11y/useFocusableInteractive: highlighted via the editor's aria-activedescendant, never focused.
          <div
            key={item.key}
            id={optionId(index)}
            ref={(element) => {
              itemRefs.current[index] = element;
            }}
            // biome-ignore lint/a11y/useSemanticElements: <option> only works inside a <select>; see the listbox above.
            role="option"
            aria-selected={index === active}
            className={styles.item}
            data-active={index === active ? "true" : undefined}
            onMouseDown={(event) => {
              event.preventDefault();
              command(item);
            }}
            onMouseEnter={() => setActive(index)}
          >
            <span className={styles.label}>{item.label}</span>
            <span className={styles.group}>{item.group}</span>
          </div>
        ))}
      </div>
    );
  },
);

AttributeSuggestionList.displayName = "AttributeSuggestionList";
